import React, { useState } from 'react'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import ChatRoomListSection from './sections/chat_room_list_section'
import ChatDetailSection from './sections/chat_detail_section'
import { useChat } from '../../hooks/use_chat'
import { useUnreadNotifications } from '../../providers/unread_notifications_provider'
import { ChatRoom } from '../../clients/kyousou_api_client'
import mountainIcon from '../../images/logo_small_white.png'
import LoadingSpinner from '../../components/loading/loading_spinner'
import { TopMessageSection } from '../../components/sections/top_message_section'
import Breadcrumb from '../../components/common/breadcrumb'

// メッセージの型定義
export type Message = {
  id: string
  senderId: string
  receiverId: string
  content: string
  timestamp: string
  isOwn: boolean
  attachment?: {
    name: string
    url: string
  }
}

/**
 * チャットページ
 * 企業間のメッセージングを管理
 * - チャット相手一覧: Laravel API
 * - メッセージデータ: Firebase Firestore
 */
const ChatPage: React.FC = () => {
  const [selectedChat, setSelectedChat] = useState<ChatRoom | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const { chats, loading, error, firebaseToken, markAsRead } = useChat()
  const { unreadNotifications } = useUnreadNotifications()

  // 検索フィルタリング
  const filteredChats = chats.filter((chat) =>
    chat.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return (
      <AuthenticatedLayout>
        <div className="flex h-[calc(100vh-64px)] items-center justify-center">
          <div className="text-center">
            <div className="text-4xl text-main-gray mb-4">
              <LoadingSpinner />
            </div>
            <p className="text-main-darkgray">読み込み中...</p>
          </div>
        </div>
      </AuthenticatedLayout>
    )
  }

  if (error) {
    return (
      <AuthenticatedLayout>
        <div className="flex h-[calc(100vh-64px)] items-center justify-center">
          <div className="text-center">
            <div className="text-4xl text-main-red mb-4">❌</div>
            <p className="text-main-red">{error}</p>
          </div>
        </div>
      </AuthenticatedLayout>
    )
  }

  return (
    <AuthenticatedLayout>
      <div className="p-4">
        <Breadcrumb
          items={[
            { label: 'ホーム', path: '/dashboard' },
            { label: 'チャット' },
          ]}
        />

        {!loading && chats.length === 0 && (
          <TopMessageSection>
            <p>マッチングした企業とのみ、メッセージを送信できます。</p>
            <p>気になる企業にはマッチングリクエストを送信してみましょう。</p>
          </TopMessageSection>
        )}
        <div className="flex h-[calc(100vh-64px)] gap-4">
          {/* 左側：チャット一覧 */}
          <div className="w-60 lg:w-80 bg-main-white rounded-lg shadow-sm border border-main-gray">
            <ChatRoomListSection
              chatRooms={filteredChats}
              selectedChat={selectedChat}
              onSelectChat={setSelectedChat}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              unreadNotifications={unreadNotifications}
            />
          </div>

          {/* 右側：チャット詳細 */}
          <div className="flex-1 bg-main-white rounded-lg shadow-sm border border-main-gray">
            {selectedChat ? (
              <ChatDetailSection
                chat={selectedChat}
                firebaseToken={firebaseToken}
                markAsRead={markAsRead}
              />
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <img
                    src={mountainIcon}
                    alt="山のアイコン"
                    className="w-16 h-16 mx-auto"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  )
}

export default ChatPage
